<script setup lang="ts">
import FinestButton from '@/components/FinestButton.vue'
import InertiaFormField from '@/components/InertiaFormField.vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const visible = defineModel<boolean>('visible', { default: false })

const emit = defineEmits<{
  created: [person: { first_name: string; last_name: string }]
}>()

const { t } = useI18n({ useScope: 'local' })

const firstName = ref('')
const lastName = ref('')

const canSubmit = computed(() => lastName.value.trim().length > 0)

function submit(): void {
  if (!canSubmit.value) {
    return
  }

  emit('created', { first_name: firstName.value.trim(), last_name: lastName.value.trim() })
  visible.value = false
}

function reset(): void {
  firstName.value = ''
  lastName.value = ''
}
</script>

<template>
  <Dialog v-model:visible="visible" modal :header="t('title')" class="w-full max-w-lg" @after-hide="reset">
    <span class="text-surface-500 mb-6 block">{{ t('subtitle') }}</span>

    <form class="flex flex-col gap-4" @submit.prevent="submit">
      <InertiaFormField :label="t('firstName')">
        <InputText v-model="firstName" autofocus fluid />
      </InertiaFormField>
      <InertiaFormField :label="t('lastName')" required>
        <InputText v-model="lastName" fluid />
      </InertiaFormField>
    </form>

    <template #footer>
      <FinestButton :label="t('cancel')" variant="ghost" @click="visible = false" />
      <FinestButton :label="t('submit')" :disabled="!canSubmit" @click="submit" />
    </template>
  </Dialog>
</template>

<i18n lang="json">
{
  "de": {
    "title": "Neue Person hinzufügen",
    "subtitle": "Lege eine neue Person als Gesellschafter an. Sie wird direkt der Liste hinzugefügt.",
    "firstName": "Vorname",
    "lastName": "Nachname",
    "cancel": "Abbrechen",
    "submit": "Hinzufügen"
  },
  "en": {
    "title": "Add new person",
    "subtitle": "Create a new person as a shareholder. They will be added to the list directly.",
    "firstName": "First name",
    "lastName": "Last name",
    "cancel": "Cancel",
    "submit": "Add"
  }
}
</i18n>
